import type { InsightCategory, InsightRaw, InsightStatus } from '../../types';

export type InsightTone = 'neutral' | 'info' | 'danger' | 'success';

export interface InsightCategoryMeta {
  label: string;
  icon: string;
  tone: InsightTone;
}

/** 4 categorias canônicas do enum InsightCategory → rótulos PT (badges-3.jsx). */
export const INSIGHT_CATEGORY_META: Record<InsightCategory, InsightCategoryMeta> = {
  operational: { label: 'Operacional', icon: '⚙', tone: 'neutral' },
  learning: { label: 'Aprendizado', icon: '💡', tone: 'info' },
  compliance: { label: 'Conformidade', icon: '🛡', tone: 'danger' },
  commercial: { label: 'Comercial', icon: '💰', tone: 'success' },
};

export type ConfidenceLevel = 'very-high' | 'high' | 'medium' | 'low';

/**
 * D2 — `confidenceLevel` é DERIVADO. O backend guarda `confidence` Decimal(4,3) (0..1), sem enum de
 * nível. Faixas: ≥0.85 very-high · ≥0.70 high · ≥0.55 medium · senão low. O piso de entrega do agente
 * é 0.45 (insights abaixo disso não são gerados), então `low` cobre [0.45, 0.55).
 */
export function deriveConfidenceLevel(confidence: number): ConfidenceLevel {
  if (confidence >= 0.85) return 'very-high';
  if (confidence >= 0.7) return 'high';
  if (confidence >= 0.55) return 'medium';
  return 'low';
}

export interface DrillDownPair {
  key: string;
  value: string;
}

/**
 * D6 — render defensivo do `drillDownData` (Json livre, categoria-específico). Objeto plano vira
 * pares key-value; valores não-primitivos são serializados curto; ausente/array/não-objeto ⇒ [].
 * NÃO assumir shape fixo.
 */
export function parseDrillDown(raw: unknown): DrillDownPair[] {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>).map(([key, value]) => ({
    key,
    value:
      value === null || value === undefined
        ? '—'
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value as string | number | boolean),
  }));
}

export interface InsightCardVM {
  id: string;
  category: InsightCategory;
  categoryLabel: string;
  content: string;
  confidence: number;
  confidenceLevel: ConfidenceLevel;
  detectorAgentId: string;
  detectionPeriod: string;
  basedOnCount: number;
  /** Item 478 — base abaixo do piso do tenant: esconder confiança, avisar. */
  insufficientBasis: boolean;
  drillDown: DrillDownPair[];
  status: InsightStatus;
  targetUserId: string | null;
  createdAt: string;
}

/** Card honesto derivado do Insight cru (D1/D2/D6). confidence string → Number. */
export function adaptInsightCard(raw: InsightRaw): InsightCardVM {
  const confidence = Number(raw.confidence);
  return {
    id: raw.id,
    category: raw.category,
    categoryLabel: INSIGHT_CATEGORY_META[raw.category]?.label ?? raw.category,
    content: raw.content,
    confidence,
    confidenceLevel: deriveConfidenceLevel(confidence),
    detectorAgentId: raw.detectorAgentId,
    detectionPeriod: raw.detectionPeriod,
    basedOnCount: raw.basedOnCount,
    insufficientBasis: raw.insufficientBasis ?? false,
    drillDown: parseDrillDown(raw.drillDownData),
    status: raw.status,
    targetUserId: raw.targetUserId,
    createdAt: raw.createdAt,
  };
}
